import React, { useContext, useEffect, useMemo, useState } from "react";
import type { ChangeEvent, ComponentType } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Button,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { toastr } from "react-redux-toastr";

import { subscriptionHelper } from "@/features/subscriptions/helpers/subscriptionHelper";
import { subscriptionService } from "@/features/subscriptions/services/subscriptionService";
import { GlobalContext, LoaderContext } from "@/helpers/contexts/contexts";
import BrandMatchCard from "./BrandMatchCard";
import LocationCalculation from "./LocationCalculation";
import TotalCalculation from "./TotalCalculation";
import TermsAndConditions from "@/components/tnc/TermsAndConditions";
import PreviousSelectedPlan from "./PreviousSelectedPlan";
import {
  BRAND_CONST,
  CANADA_COUNTRY_VALUES,
  CANADA_PAYMENT_METHOD,
  HTTP_STATUS,
  subscriptionPlanPaymentType,
} from "@/config/constants";
import { stripeMicroService } from "@/shared/services/stripeMicroService";
import ConfirmStripeEmbedCheckoutPopup from "@/components/stripe/embedCheckout/ConfirmStripeEmbedCheckoutPopup";
import { logger } from "@/helpers/loggerHelper";
import { ROUTES } from "@/helpers/routes/routePaths";
import { grantThankYouAccessForFlow } from "@/shared/helpers/thankYouAccessHelper";

type SubscriptionPackage = {
  currency?: string;
  id?: string | number;
  is_brand_match?: number;
  payment_type?: string;
  [key: string]: unknown;
};

type SubscriptionLocation = {
  brand_auto_renew?: boolean;
  country?: string;
  current_package_payment_type?: string;
  fk_user_id?: string | number;
  packageData?: unknown;
  system_generated_location_id?: string | number;
  upcoming_package_id?: string | number;
  [key: string]: unknown;
};

type SubscriptionSelection = {
  country?: string;
  locationId?: string | number;
  locationInfo?: SubscriptionLocation;
  packageId?: string | number;
  packageInfo?: SubscriptionPackage;
  [key: string]: unknown;
};

type ConfirmPackageSelectionProps = {
  isChangingUpcomingPackage?: boolean;
  isOptIn?: boolean;
  onConfirm: (_refresh?: boolean) => void;
  onPrev: () => void;
  selections?: SubscriptionSelection[];
  setActiveStep?: (..._args: unknown[]) => void;
};

type SubscriptionContext = {
  loggedInUser: {
    email?: string;
    id?: string | number;
  };
  selectedBrand?: {
    brand_name?: string;
    id?: string | number;
    is_white_label?: boolean;
    system_generated_brand_id?: string | number;
  } | null;
};

type ServiceResponse = {
  data?: {
    data?: {
      campaign_id?: string | number | null;
      is_redirect?: boolean;
    };
  };
  status?: number;
};

type ServiceError = {
  response?: {
    status?: number;
  };
};

type SavePayload = Record<string, unknown> & {
  is_changing?: boolean;
  is_optIn?: boolean;
  selections?: SubscriptionSelection[];
};

const FormControlLabelComponent = FormControlLabel as ComponentType<any>;
const TypographyComponent = Typography as ComponentType<any>;
const ConfirmStripeEmbedCheckoutPopupComponent =
  ConfirmStripeEmbedCheckoutPopup as ComponentType<any>;

export default function ConfirmPackageSelection({
  selections = [],
  onPrev,
  isChangingUpcomingPackage,
  isOptIn,
  // isNewSubscription,
  setActiveStep,
  onConfirm,
}: ConfirmPackageSelectionProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const loader = useContext(LoaderContext);
  const { loggedInUser, selectedBrand } = useContext(
    GlobalContext
  ) as SubscriptionContext;
  const [isLoading, setIsLoading] = useState(false);
  const [makePayment, setMakePayment] = useState(null);
  const [isTnCChecked, setIsTnCChecked] = useState(false);
  const [isCanadaAchFlow, setIsCanadaAchFlow] = useState(false);
  const showFullPageLoader = location.pathname === ROUTES.SUBSCRIPTION_PACKAGES;

  useEffect(() => {
    loader(showFullPageLoader && isLoading);
  }, [isLoading, loader, showFullPageLoader]);

  const paymentType = useMemo(() => {
    return selections?.[0]?.packageInfo?.payment_type;
  }, [selections]);

  const currentPackagePaymentType = useMemo(() => {
    return selections?.[0]?.locationInfo?.current_package_payment_type;
  }, [selections]);

  const isCanadaLocation = useMemo(() => {
    return selections?.every((selection) =>
      CANADA_COUNTRY_VALUES.includes(
        String(selection?.country || selection?.locationInfo?.country || "")
          .trim()
          .toUpperCase()
      )
    );
  }, [selections]);

  const isBrandAutoOn = useMemo(() => {
    return selections?.[0]?.locationInfo?.brand_auto_renew;
  }, [selections]);

  const showCadPackageAmount = useMemo(() => {
    return selections.some((selection) =>
      subscriptionHelper.hasUsdCadCurrency(selection?.packageInfo)
    );
  }, [selections]);

  const showCanadaAchFlowOption =
    (location.pathname === ROUTES.SUBSCRIPTION_PACKAGES ||
      location.pathname === ROUTES.MANAGE_SUBSCRIPTION) &&
    isCanadaLocation &&
    showCadPackageAmount &&
    (currentPackagePaymentType || paymentType) ===
      subscriptionPlanPaymentType.STRIPE;

  const isCanadaAchSelected = showCanadaAchFlowOption && isCanadaAchFlow;

  const canadaPaymentPayload = useMemo(() => {
    if (!showCanadaAchFlowOption) return {};

    return isCanadaAchSelected ? { canada_ach: true } : { canada_card: true };
  }, [isCanadaAchSelected, showCanadaAchFlowOption]);

  const totals = useMemo(() => {
    return subscriptionHelper.calculateAllSubscriptionTotals(selections);
  }, [selections]);

  const cadTotals = useMemo(() => {
    return showCadPackageAmount
      ? subscriptionHelper.calculateAllSubscriptionTotals(selections, true)
      : null;
  }, [selections, showCadPackageAmount]);

  const handleCanadaPaymentMethodChange = (
    _event: ChangeEvent<HTMLInputElement>,
    value: string
  ) => {
    setIsCanadaAchFlow(value === CANADA_PAYMENT_METHOD.PAD);
  };

  const save = async (data: SavePayload) => {
    setIsLoading(true);
    try {
      const res = (await subscriptionService.saveSubscriptionSelection(
        data
      )) as ServiceResponse;
      if (res.status === HTTP_STATUS.OK) {
        loader(false);
        const { is_redirect = false, campaign_id = null } =
          res.data?.data || {};
        grantThankYouAccessForFlow({
          stateFrom: "package_selection",
        });
        navigate(ROUTES.THANK_YOU, {
          replace: true,
          state: {
            from: "package_selection",
            is_redirect,
            campaign_id,
          },
        });
      }
    } catch (err) {
      logger.error("Error in saveSubscriptionSelection", { err });
    } finally {
      setIsLoading(false);
    }
  };

  const saveChangeOnly = async (data: SavePayload = {}) => {
    setIsLoading(true);
    try {
      const locSelectionData = selections?.[0];
      const reqData = {
        fk_location_id: locSelectionData?.locationId,
        fk_brand_id: selectedBrand?.id,
        fk_user_id: locSelectionData.locationInfo?.fk_user_id,
        new_package_id: locSelectionData?.packageId,
        system_generated_location_id:
          locSelectionData?.locationInfo?.system_generated_location_id,
        ...data,
      };

      const res = (await subscriptionService.changePackageDetails(
        reqData
      )) as ServiceResponse;
      if (res.status === HTTP_STATUS.OK) {
        loader(false);
        toastr.success("Success", `Upcoming Plan Updated Successfully`);
        onConfirm();
      }
    } catch (err) {
      const error = err as ServiceError;
      if (error.response?.status === 406) {
        loader(false);
        onConfirm();
      }
    } finally {
      setIsLoading(false);
    }
  };

  const saveOptIn = async (data: SavePayload) => {
    setIsLoading(true);
    try {
      const res = (await subscriptionService.optInAutoRenewal(
        data
      )) as ServiceResponse;
      if (res.status === HTTP_STATUS.OK) {
        loader(false);
        toastr.success(
          "Success",
          `You have Opted-In for Auto-Renew Upcoming Subscription Successfully`
        );
        onConfirm(true);
      }
    } catch (err) {
      const error = err as ServiceError;
      if (error.response?.status === 406) {
        loader(false);
        onConfirm();
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirm = () => {
    const isInvoice = paymentType === subscriptionPlanPaymentType.INVOICE;
    // const isStripe = paymentType === subscriptionPlanPaymentType.STRIPE;

    // if (isChangingUpcomingPackage && isInvoice) {
    //   return saveChangeOnly();
    // }

    const selectionData = selections.map((s) => {
      const { startDate, endDate } =
        subscriptionHelper.calculateSubscriptionDate(
          s?.packageInfo,
          s?.locationInfo as any
        );

      // eslint-disable-next-line no-unused-vars
      const { packageData: _packageData, ...locationInfoWithoutPackageData } =
        s?.locationInfo || {};

      return {
        ...s,
        startDate: startDate.format("MMM D, YYYY"),
        endDate: endDate.format("MMM D, YYYY"),
        locationInfo: locationInfoWithoutPackageData,
      };
    });

    const dataToSave: SavePayload = {
      userId: selectionData?.[0]?.locationInfo?.fk_user_id,
      created_by: loggedInUser.id,
      selections: selectionData,
      brandId: selectedBrand?.id,
      system_generated_brand_id: selectedBrand?.system_generated_brand_id,
      fk_brand_name: selectedBrand?.brand_name,
      payment_using_credit_card: paymentType,
      payment_type: paymentType,
      is_white_label: selectedBrand?.is_white_label,
    };

    if (
      !isBrandAutoOn &&
      selectionData?.[0]?.locationInfo?.upcoming_package_id
    ) {
      dataToSave.is_changing = true;
      dataToSave.is_optIn = false;
    }

    if (isChangingUpcomingPackage) {
      dataToSave.is_changing = true;
      dataToSave.is_optIn = false;
    }

    if (isOptIn) {
      dataToSave.is_changing = false;
      dataToSave.is_optIn = true;
    }

    if (isInvoice && isChangingUpcomingPackage) {
      return saveChangeOnly(dataToSave);
    } else if (isInvoice && isOptIn && isBrandAutoOn) {
      saveOptIn(dataToSave);
    } else if (paymentType === subscriptionPlanPaymentType.STRIPE) {
      setMakePayment({
        ...dataToSave,
        amount: showCadPackageAmount ? cadTotals?.total : totals.total,
        ...canadaPaymentPayload,
      });
    } else {
      save(dataToSave);
    }
  };

  const isBrandMatch = selections.some(
    (check) => check?.packageInfo?.is_brand_match === 1
  );

  const subscriptionReturnUri = useMemo(() => {
    let route = ROUTES.SUBSCRIPTION_RETURN;

    if (showCanadaAchFlowOption) {
      route = isCanadaAchSelected
        ? ROUTES.SUBSCRIPTION_PAD_RETURN
        : ROUTES.SUBSCRIPTION_CAD_RETURN;
    }

    return isOptIn || isChangingUpcomingPackage
      ? `${route}?manage_subscription=true`
      : route;
  }, [
    isCanadaAchSelected,
    isChangingUpcomingPackage,
    isOptIn,
    showCanadaAchFlowOption,
  ]);

  return (
    <div className="confirm-package-selection py-5">
      <div className="container">
        <Typography
          component="h2"
          variant="h5"
          gutterBottom
          className="sub-heading"
          color="text.primary"
        >
          Review and Confirm
        </Typography>

        {isChangingUpcomingPackage
          ? selections.map((locData) => (
              <PreviousSelectedPlan
                data={locData}
                key={locData.locationId}
                isChangingUpcomingPackage={isChangingUpcomingPackage}
              />
            ))
          : null}

        {selectedBrand?.id === BRAND_CONST.KUMON_BRAND_ID &&
        isBrandMatch &&
        !isChangingUpcomingPackage ? (
          <BrandMatchCard selections={selections} />
        ) : null}

        {selections.map((loc) => (
          <LocationCalculation
            data={loc}
            key={loc.locationId}
            isChangingUpcomingPackage={isChangingUpcomingPackage}
            showCadAmount={showCadPackageAmount}
          />
        ))}
        {totals && (
          <div>
            <TotalCalculation
              totals={totals}
              cadTotals={cadTotals}
              showCadAmount={showCadPackageAmount}
            />
          </div>
        )}
        <TermsAndConditions
          isChecked={isTnCChecked}
          handleTermsAndCondition={(e) => setIsTnCChecked(e.target.checked)}
        />
        {showCanadaAchFlowOption ? (
          <div className="row mt-2">
            <div className="col-12 d-flex justify-content-end">
              <div className="d-flex align-items-center">
                <TypographyComponent
                  component="div"
                  variant="title"
                  className="mr-4 mb-2"
                >
                  Payment method:
                </TypographyComponent>
                <RadioGroup
                  row
                  name="canadaPaymentMethod"
                  value={
                    isCanadaAchFlow
                      ? CANADA_PAYMENT_METHOD.PAD
                      : CANADA_PAYMENT_METHOD.CREDIT_CARD
                  }
                  onChange={handleCanadaPaymentMethodChange}
                >
                  <FormControlLabelComponent
                    value={CANADA_PAYMENT_METHOD.CREDIT_CARD}
                    control={<Radio color="primary" />}
                    label="Credit card"
                    componentsProps={{ typography: { variant: "body2" } }}
                  />
                  <FormControlLabelComponent
                    value={CANADA_PAYMENT_METHOD.PAD}
                    control={<Radio color="primary" />}
                    label="PAD (Pre-Authorized Debit)"
                    componentsProps={{ typography: { variant: "body2" } }}
                  />
                </RadioGroup>
              </div>
            </div>
          </div>
        ) : null}
        <div className="row my-4 align-items-center">
          <div className="col-md-8 mb-4 note">
            Once confirmed, your plan will be updated and you will be able to
            make changes until your billing date.
          </div>

          <div
            className={`col-md-4 mb-4 d-flex align-items-center justify-content-end`}
          >
            <Button color="primary" variant="outlined" onClick={onPrev}>
              Make Changes
            </Button>
            <Button
              variant="contained"
              color="primary"
              className="ml-2 px-5"
              onClick={handleConfirm}
              disabled={!isTnCChecked}
            >
              Confirm
            </Button>
          </div>
        </div>
      </div>

      <ConfirmStripeEmbedCheckoutPopupComponent
        setActiveStep={setActiveStep}
        title="Payment"
        makePayment={makePayment ? true : false}
        data={
          makePayment
            ? {
                email: loggedInUser.email,
                ...makePayment,
              }
            : {}
        }
        onClose={() => setMakePayment(null)}
        return_uri={subscriptionReturnUri}
        api={stripeMicroService.subscriptionPayment}
        locSubscription={true}
      />
    </div>
  );
}
